import uuid from 'uuid';
import { isNil } from 'lodash';
import {
  IndexedInteractionStepResponse,
  InteractionStepType,
  MultiTurnInteractionConfiguration,
  TurnContinuationContext,
  StepParamsForTypeWithStage,
} from '@scale/shared/types/chat';

import { ChatTaskParamsStages } from '@scale/shared/chat/steps';

export interface StepParamsAuxiliary {
  stage: ChatTaskParamsStages;
  responseIndex: number;
  turn: number;
}

/**
 * Calculates the array of step params for the given responses and configuration.
 */
export function calculateStepParams(
  _responses: IndexedInteractionStepResponse[],
  config: MultiTurnInteractionConfiguration,
): (StepParamsForTypeWithStage<InteractionStepType> & StepParamsAuxiliary)[] {
  const stepParams: StepParamsForTypeWithStage<InteractionStepType>[] = [];
  const responses = [..._responses];
  const turnLength = config.turn.length;
  const beforeLength = config.before.length;
  let responseIndex = 0;
  let turnCounter = 0;

  const _promptInputStepId = config.before.find(
    step => step.type === InteractionStepType.PromptInput,
  );
  const promptInputStepId = _promptInputStepId?.id ?? uuid.v4();

  function getParamForStageIndex(stage: ChatTaskParamsStages, index: number) {
    const param = {
      ...config[stage][index],
      stage,
      responseIndex,
      turn: stage === ChatTaskParamsStages.Turn ? turnCounter : -1,
    };
    responseIndex++;
    return param;
  }

  const pushParam = (stage: ChatTaskParamsStages, index: number) => {
    if (stage === ChatTaskParamsStages.Turn && index === 0 && config.turn.length === 0) {
      const param = getMultiTurnContinueParam();
      stepParams.push(param);
      return;
    }
    const paramToPush = getParamForStageIndex(stage, index);
    if (isNil(paramToPush.id)) return;
    stepParams.push(paramToPush);
  };

  const getMultiTurnContinueParam = (): StepParamsForTypeWithStage<InteractionStepType> => {
    const param: StepParamsForTypeWithStage<InteractionStepType> = {
      id: promptInputStepId,
      type: InteractionStepType.MultiTurnContinue,
      params: {
        instructions: 'Continue with the prompt or end your session.',
      },
      stage: ChatTaskParamsStages.Turn,
      responseIndex,
      turn: turnCounter,
    };
    responseIndex++;
    return param;
  };

  let turnIndex = 0;
  let afterIndex = 0;
  let stage: ChatTaskParamsStages = ChatTaskParamsStages.Before as ChatTaskParamsStages; // NOTE: This cast prevents type narrowing!

  // First construct stepParams based on responses
  responses.forEach((response, index) => {
    /*
     * Transition to the turn stage if all before steps are completed. This stage
     * change is safe because we are processing the response at this index in this iteration.
     */
    if (stage === ChatTaskParamsStages.Before && index === beforeLength) {
      stage = ChatTaskParamsStages.Turn;
    }

    /*
     * Update the relevant turn variables for the turn stage. When we reach a
     * MultiTurnContinue step, we increment the turnCounter and reset the turnIndex
     * to begin the next turn.
     */
    if (
      stage === ChatTaskParamsStages.Turn &&
      response.type === InteractionStepType.MultiTurnContinue
    ) {
      stepParams.push(getMultiTurnContinueParam());
      turnCounter++;
      turnIndex = 0;
    }

    /*
     * Push the relevant params based on the stage and relevant index.
     * For turn and after stages, increment the stage index.
     * Since before is the first stage, we can use the overall index.
     */
    if (stage === ChatTaskParamsStages.Before) {
      pushParam(ChatTaskParamsStages.Before, index);
    }

    if (
      stage === ChatTaskParamsStages.Turn &&
      response.type !== InteractionStepType.MultiTurnContinue
    ) {
      pushParam(ChatTaskParamsStages.Turn, turnIndex);
      turnIndex++;
    }

    if (stage === ChatTaskParamsStages.After) {
      pushParam(ChatTaskParamsStages.After, afterIndex);
      afterIndex++;
    }

    /* Transition to the after stage if the final turn has been completed.
    /* We need to do this at the end because we already have processed the MultiTurnContinue step
     * in this iteration.
     */
    if (
      stage === ChatTaskParamsStages.Turn &&
      response.type === InteractionStepType.MultiTurnContinue &&
      !(response.context as TurnContinuationContext).shouldContinue
    ) {
      stage = ChatTaskParamsStages.After;
    }
  });

  /*
   * At this point, we have constructed all the stepParams available based on the responses.
   * To render the next step that needs to be completed, we compute and insert its params at
   * the end of the array.
   */

  // For before steps, we either add the next before step or the first step of a new turn
  if (stage === ChatTaskParamsStages.Before && stepParams.length < beforeLength) {
    pushParam(ChatTaskParamsStages.Before, stepParams.length);
  } else if (stage === ChatTaskParamsStages.Before && stepParams.length === beforeLength) {
    pushParam(ChatTaskParamsStages.Turn, 0);
  }

  // For turn steps, we either add the next turn step or the final MultiTurnContinue step
  else if (stage === ChatTaskParamsStages.Turn && turnIndex !== turnLength) {
    pushParam(ChatTaskParamsStages.Turn, turnIndex);
  } else if (stage === ChatTaskParamsStages.Turn && turnIndex === turnLength) {
    stepParams.push(getMultiTurnContinueParam());
  }

  // For after steps, we add the next after step
  else if (stage === ChatTaskParamsStages.After && afterIndex !== config.after.length) {
    pushParam(ChatTaskParamsStages.After, afterIndex);
  }

  return stepParams;
}
